import React, { useState, useCallback } from 'react';
import { Volume2, AlertTriangle, Users, Clock, Calendar, Activity } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';

interface NoiseEvent {
  timestamp: Date;
  level: number;
  cause: string;
  duration: number;
  occupants: number;
  activities: string[];
}

interface RoomNoiseData {
  id: string;
  name: string;
  currentLevel: number;
  averageLevel: number;
  maxLevel: number;
  threshold: number;
  historicalData: {
    time: string;
    level: number;
  }[];
  noiseEvents: NoiseEvent[];
  patientType: string;
  recoveryPhase: string;
}

interface NoiseAnalyticsProps {
  buildingData: {
    rooms: {
      all: any[];
    };
    environment: {
      temperature: number;
      humidity: number;
      co2: number;
      noise: number;
    };
  };
}

const generateMockData = (): RoomNoiseData[] => {
  const rooms: RoomNoiseData[] = [];
  const now = new Date();
  
  for (let i = 1; i <= 6; i++) {
    const historicalData = [];
    const noiseEvents = [];
    const baseLevel = 35 + Math.random() * 10;
    
    // Generate 24 hours of data points (every 30 minutes)
    for (let h = 0; h < 48; h++) {
      const time = new Date(now.getTime() - (24 * 60 * 60 * 1000) + (h * 30 * 60 * 1000));
      const level = baseLevel + Math.random() * 20;
      
      historicalData.push({
        time: time.toLocaleTimeString(),
        level: Math.round(level)
      });

      // Generate noise events for significant spikes
      if (level > 55) {
        noiseEvents.push({
          timestamp: time,
          level: Math.round(level),
          cause: ['Staff Rounds', 'Equipment Maintenance', 'Visitor Activity', 'Medical Procedure', 'Room Cleaning'][Math.floor(Math.random() * 5)],
          duration: Math.round(10 + Math.random() * 20),
          occupants: Math.floor(2 + Math.random() * 4),
          activities: ['Conversation', 'Equipment Operation', 'Door Movement', 'Cart Transportation'].sort(() => Math.random() - 0.5).slice(0, 2)
        });
      }
    }

    rooms.push({
      id: `ROOM_${i}`,
      name: `Patient Room ${i}`,
      currentLevel: Math.round(baseLevel + Math.random() * 15),
      averageLevel: Math.round(baseLevel),
      maxLevel: Math.round(baseLevel + 20),
      threshold: 55,
      historicalData,
      noiseEvents,
      patientType: ['Post-Surgery', 'Intensive Care', 'Recovery', 'Long-term Care'][Math.floor(Math.random() * 4)],
      recoveryPhase: ['Critical', 'Stable', 'Improving', 'Final Stage'][Math.floor(Math.random() * 4)]
    });
  }
  
  return rooms;
};

const NoiseAnalytics: React.FC<NoiseAnalyticsProps> = ({ buildingData }) => {
  const [rooms] = useState<RoomNoiseData[]>(generateMockData());
  const [selectedRoom, setSelectedRoom] = useState<RoomNoiseData | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<NoiseEvent | null>(null);

  const handleChartClick = useCallback((room: RoomNoiseData, data: any) => {
    const event = room.noiseEvents.find(e => 
      e.timestamp.toLocaleTimeString() === data.activeLabel
    );
    if (event) {
      setSelectedEvent(event);
    }
  }, []);

  const getLevelColor = (level: number, threshold: number) => {
    if (level >= threshold) return 'text-red-600';
    if (level >= threshold - 10) return 'text-yellow-600';
    return 'text-green-600';
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Ambient Noise Analytics</h2>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-600">Recommended Max Level: 55 dB</span>
          <AlertTriangle className="text-yellow-500" size={20} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {rooms.map(room => (
          <div 
            key={room.id}
            className="bg-white rounded-lg shadow-md p-6 cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => setSelectedRoom(room)}
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="font-semibold text-lg">{room.name}</h3>
                <p className="text-sm text-gray-600">{room.patientType} - {room.recoveryPhase}</p>
              </div>
              <div className={`text-right ${getLevelColor(room.currentLevel, room.threshold)}`}>
                <div className="text-2xl font-bold">{room.currentLevel} dB</div>
                <div className="text-sm">Current Level</div>
              </div>
            </div>

            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={room.historicalData}
                  onClick={(data) => handleChartClick(room, data)}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="time" 
                    interval={8}
                    tickFormatter={(time) => {
                      const date = new Date(`2000/01/01 ${time}`);
                      return date.toLocaleTimeString([], { hour: 'numeric' });
                    }}
                  />
                  <YAxis domain={[30, 70]} />
                  <Tooltip 
                    formatter={(value: number) => [`${value} dB`, 'Noise Level']}
                    labelFormatter={(label) => `Time: ${label}`}
                  />
                  <ReferenceLine y={room.threshold} stroke="red" strokeDasharray="3 3" />
                  <Line
                    type="monotone"
                    dataKey="level"
                    stroke="#2563eb"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
              <div>
                <div className="text-gray-600">Average</div>
                <div className="font-semibold">{room.averageLevel} dB</div>
              </div>
              <div>
                <div className="text-gray-600">Max</div>
                <div className="font-semibold">{room.maxLevel} dB</div>
              </div>
              <div>
                <div className="text-gray-600">Events</div>
                <div className="font-semibold">{room.noiseEvents.length}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Noise Event Details Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-semibold">Noise Event Details</h3>
              <button 
                onClick={() => setSelectedEvent(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Volume2 className="text-red-500" />
                  <div>
                    <div className="text-sm text-gray-600">Noise Level</div>
                    <div className="font-semibold">{selectedEvent.level} dB</div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Clock />
                  <div>
                    <div className="text-sm text-gray-600">Duration</div>
                    <div className="font-semibold">{selectedEvent.duration} minutes</div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Users />
                  <div>
                    <div className="text-sm text-gray-600">Occupants</div>
                    <div className="font-semibold">{selectedEvent.occupants} people</div>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <div className="text-sm text-gray-600">Primary Cause</div>
                  <div className="font-semibold">{selectedEvent.cause}</div>
                </div>

                <div>
                  <div className="text-sm text-gray-600">Activities</div>
                  <div className="space-y-1">
                    {selectedEvent.activities.map((activity, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <Activity size={14} />
                        <span>{activity}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 p-4 bg-yellow-50 rounded-lg">
              <h4 className="font-semibold mb-2">Recommendations</h4>
              <ul className="list-disc list-inside space-y-2 text-sm">
                <li>Schedule non-essential activities during designated quiet hours</li>
                <li>Use visual indicators to remind staff and visitors of noise levels</li>
                <li>Consider equipment maintenance scheduling optimization</li>
                <li>Implement soft-close mechanisms on doors and drawers</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NoiseAnalytics; 
